/**
 * TBGenSurv Module 2 — FHIR R4 Utilities
 * 
 * All lab results are structured as FHIR R4 resources.
 * This ensures interoperability with:
 *   - DHIS2 (Zimbabwe MoHCC national system)
 *   - OpenMRS (used at many Zimbabwean hospitals)
 *   - OpenHIE compliant systems
 *   - WHO GLASS (Global Antimicrobial Resistance Surveillance)
 *   - Africa CDC Pathogen Genomics Initiative
 * 
 * LOINC codes used for standardised lab observations.
 * SNOMED CT used for clinical findings.
 */

// ── LOINC CODES FOR TB LAB TESTS ──────────────────────────────────────
export const LOINC = {
  // Smear microscopy
  SMEAR_MICROSCOPY:       '11545-1',  // Microscopy study
  AFB_SMEAR:              '11483-5',  // AFB smear
  
  // GeneXpert MTB/RIF
  GENEXPERT_MTB:          '89959-2',  // MTB DNA [Presence] in Specimen by NAA with probe detection
  GENEXPERT_RIF:          '89953-5',  // Rifampin resistance [Susceptibility] by Molecular method
  
  // Culture
  MTB_CULTURE:            '17296-4',  // Mycobacterium sp identified in Specimen by Culture
  
  // DST - Drug susceptibility
  DST_ISONIAZID:          '20389-5',  // Isoniazid susceptibility
  DST_RIFAMPICIN:         '20380-4',  // Rifampicin susceptibility
  DST_PYRAZINAMIDE:       '20401-8',  // Pyrazinamide susceptibility
  DST_ETHAMBUTOL:         '20374-7',  // Ethambutol susceptibility
  DST_STREPTOMYCIN:       '20406-7',  // Streptomycin susceptibility
  DST_MOXIFLOXACIN:       '56046-1',  // Moxifloxacin susceptibility
  DST_BEDAQUILINE:        '73902-2',  // Bedaquiline susceptibility
  
  // WGS
  WGS_REQUESTED:          '81247-9',  // Master HL7 genetic variant reporting panel
};

// ── SNOMED CT CODES ───────────────────────────────────────────────────
export const SNOMED = {
  PULMONARY_TB:           '154283005',
  MDR_TB:                 '431781000124103',
  XDR_TB:                 '707447009',
  SUSCEPTIBLE:            '131196009',
  RESISTANT:              '264841006',
  MTB_DETECTED:           '703119002',
  MTB_NOT_DETECTED:       '260385009',
};

// ── GENERATE FHIR DIAGNOSTIC REPORT ──────────────────────────────────
export function buildFHIRDiagnosticReport(sample, labResult, patient) {
  const now = new Date().toISOString();
  
  const observations = [];
  
  // Smear microscopy
  if (labResult.smear_result) {
    observations.push({
      resourceType: 'Observation',
      id:           `obs-smear-${sample.id}`,
      status:       'final',
      category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'laboratory' }] }],
      code: { coding: [{ system: 'http://loinc.org', code: LOINC.AFB_SMEAR, display: 'AFB Smear Microscopy' }] },
      subject: { reference: `Patient/${patient.deident_hash}` },
      effectiveDateTime: labResult.smear_date || now,
      valueCodeableConcept: {
        coding: [{
          system: 'http://snomed.info/sct',
          code:   labResult.smear_result === 'Positive' ? SNOMED.MTB_DETECTED : SNOMED.MTB_NOT_DETECTED,
          display: labResult.smear_result,
        }]
      }
    });
  }

  // GeneXpert
  if (labResult.genexpert_result) {
    observations.push({
      resourceType: 'Observation',
      id:           `obs-gxpert-${sample.id}`,
      status:       'final',
      category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'laboratory' }] }],
      code: { coding: [{ system: 'http://loinc.org', code: LOINC.GENEXPERT_MTB, display: 'GeneXpert MTB/RIF' }] },
      subject: { reference: `Patient/${patient.deident_hash}` },
      effectiveDateTime: labResult.genexpert_date || now,
      component: [
        {
          code: { coding: [{ system: 'http://loinc.org', code: LOINC.GENEXPERT_MTB, display: 'MTB Detection' }] },
          valueString: labResult.genexpert_result,
        },
        {
          code: { coding: [{ system: 'http://loinc.org', code: LOINC.GENEXPERT_RIF, display: 'Rifampicin Resistance' }] },
          valueString: labResult.genexpert_rif_resist || 'NOT TESTED',
        }
      ]
    });
  }

  // DST results
  const dstMap = [
    { key: 'dst_isoniazid',    loinc: LOINC.DST_ISONIAZID,   drug: 'Isoniazid'    },
    { key: 'dst_rifampicin',   loinc: LOINC.DST_RIFAMPICIN,  drug: 'Rifampicin'   },
    { key: 'dst_pyrazinamide', loinc: LOINC.DST_PYRAZINAMIDE,drug: 'Pyrazinamide' },
    { key: 'dst_ethambutol',   loinc: LOINC.DST_ETHAMBUTOL,  drug: 'Ethambutol'   },
  ];

  dstMap.forEach(({ key, loinc, drug }) => {
    if (labResult[key]) {
      observations.push({
        resourceType: 'Observation',
        id:           `obs-dst-${drug.toLowerCase()}-${sample.id}`,
        status:       'final',
        category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'laboratory' }] }],
        code: { coding: [{ system: 'http://loinc.org', code: loinc, display: `${drug} Susceptibility` }] },
        subject: { reference: `Patient/${patient.deident_hash}` },
        effectiveDateTime: labResult.dst_date || now,
        valueCodeableConcept: {
          coding: [{
            system: 'http://snomed.info/sct',
            code:   labResult[key] === 'Resistant' ? SNOMED.RESISTANT : SNOMED.SUSCEPTIBLE,
            display: labResult[key],
          }]
        }
      });
    }
  });

  // DiagnosticReport (wraps all observations)
  return {
    resourceType: 'DiagnosticReport',
    id:           `dr-${sample.id}`,
    status:       'final',
    category: [{
      coding: [{ system: 'http://terminology.hl7.org/CodeSystem/v2-0074', code: 'MB', display: 'Microbiology' }]
    }],
    code: {
      coding: [{ system: 'http://loinc.org', code: '11502-2', display: 'Laboratory report' }],
      text: 'TB Laboratory Diagnostic Report',
    },
    subject: { reference: `Patient/${patient?.deident_hash || 'unknown'}` },
    specimen: [{ reference: `Specimen/${sample.id}`, display: sample.sample_code }],
    effectiveDateTime: now,
    issued: now,
    performer: [{ display: 'National Microbiology Reference Laboratory, Zimbabwe' }],
    result:   observations.map(o => ({ reference: `#${o.id}` })),
    contained: observations,
    conclusion: labResult.tb_classification || 'Pending classification',
    conclusionCode: labResult.tb_classification ? [{
      coding: [{
        system: 'http://snomed.info/sct',
        code:   labResult.tb_classification === 'MDR-TB' ? SNOMED.MDR_TB : SNOMED.PULMONARY_TB,
        display: labResult.tb_classification,
      }]
    }] : [],
    extension: [{
      url: 'https://tbgensurv.uz.ac.zw/fhir/StructureDefinition/wgs-requested',
      valueBoolean: labResult.wgs_requested || false,
    }],
  };
}

// ── DHIS2 DATA VALUE SET ──────────────────────────────────────────────
// Format results for posting to DHIS2 Zimbabwe national instance
export function buildDHIS2DataValueSet(sample, labResult, orgUnitId) {
  const period = new Date().toISOString().slice(0, 7).replace('-', ''); // YYYYMM
  return {
    dataSet:    'TB_LAB_RESULTS_DS',
    completeDate: new Date().toISOString().slice(0, 10),
    period,
    orgUnit:    orgUnitId || 'NMRL_HQ',
    dataValues: [
      { dataElement: 'DE_TB_GX_RESULT',     value: labResult.genexpert_result || '' },
      { dataElement: 'DE_TB_RIF_RESIST',    value: labResult.genexpert_rif_resist || '' },
      { dataElement: 'DE_TB_DST_INH',       value: labResult.dst_isoniazid || '' },
      { dataElement: 'DE_TB_DST_RIF',       value: labResult.dst_rifampicin || '' },
      { dataElement: 'DE_TB_CLASSIFICATION',value: labResult.tb_classification || '' },
      { dataElement: 'DE_TB_WGS_REQUESTED', value: labResult.wgs_requested ? '1' : '0' },
      { dataElement: 'DE_TB_SAMPLE_CODE',   value: sample.sample_code },
    ],
  };
}

// ── TB CLASSIFICATION LOGIC ───────────────────────────────────────────
export function classifyTB(labResult) {
  const { dst_isoniazid: inh, dst_rifampicin: rif, genexpert_rif_resist: gxRif } = labResult;
  
  const rifResistant = rif === 'Resistant' || gxRif === 'DETECTED';
  const inhResistant = inh === 'Resistant';

  if (rifResistant && inhResistant) return { classification: 'MDR-TB',     wgs: true,  alert: 'critical', color: '#DC2626' };
  if (rifResistant)                  return { classification: 'RR-TB',      wgs: true,  alert: 'critical', color: '#DC2626' };
  if (inhResistant)                  return { classification: 'HR-TB',      wgs: false, alert: 'high',     color: '#F59E0B' };
  if (labResult.genexpert_result === 'MTB DETECTED') return { classification: 'DS-TB', wgs: false, alert: 'normal', color: '#16A34A' };
  return { classification: 'Pending', wgs: false, alert: 'none', color: '#64748B' };
}

// ── WGS TRIGGER RULES ─────────────────────────────────────────────────
export function shouldTriggerWGS(labResult) {
  const reasons = [];
  if (labResult.dst_rifampicin === 'Resistant')        reasons.push('Rifampicin resistance detected (DST)');
  if (labResult.genexpert_rif_resist === 'DETECTED')   reasons.push('Rifampicin resistance detected (GeneXpert)');
  if (labResult.dst_isoniazid === 'Resistant')         reasons.push('Isoniazid resistance detected');
  if (labResult.tb_classification === 'MDR-TB')        reasons.push('MDR-TB classification');
  if (labResult.tb_classification === 'Pre-XDR-TB')    reasons.push('Pre-XDR-TB classification');
  if (labResult.tb_classification === 'XDR-TB')        reasons.push('XDR-TB classification');
  return { triggered: reasons.length > 0, reasons };
}
